
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import {
  CodeBlock,
  ComponentDocs,
  Note,
  Preview,
} from "@lighting-beetle/lighter-styleguide";
import Bar, { BarItem } from "./../Bar";
import Button from "./../Button";
import Icon from "./../Icon";
import Table from "./../Table";
import Tooltip from "./index";

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};
const Code = makeShortcode("Code");
const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1>{`Tooltip`}</h1>
    <h2>{`Example`}</h2>
    <Tooltip id="tooltip-example" dialog="But I do provide further explanation" mdxType="Tooltip">
  This is not clear enough
    </Tooltip>
    <h2>{`Variants`}</h2>
    <h3>{`Basic`}</h3>
    <Preview mdxType="Preview">
  <Tooltip id="tooltip-1" dialog="But I do provide further explanation" mdxType="Tooltip">
    This is not clear enough
  </Tooltip>
    </Preview>
    <h3>{`Custom markup in tooltip dialog`}</h3>
    <Preview mdxType="Preview">
  <Tooltip id="tooltip-2" dialog={<Bar mdxType="Bar">
        <BarItem mdxType="BarItem">
          <Icon name="heart" mdxType="Icon" />
        </BarItem>
        <BarItem mdxType="BarItem">
          <p>Love is on my left side</p>
        </BarItem>
      </Bar>} mdxType="Tooltip">
    This is not clear enough
  </Tooltip>
    </Preview>
    <h3>{`Custom markup in tooltip trigger`}</h3>
    <Preview mdxType="Preview">
  <Tooltip id="tooltip-3" dialog="This explains a lot" mdxType="Tooltip">
    <Button mdxType="Button">You might not know what does this button do</Button>
  </Tooltip>
    </Preview>
    <h2>{`Props`}</h2>
    <ComponentDocs title="<Tooltip />" path="Tooltip/Tooltip.js" mdxType="ComponentDocs" />
    <Note title="Conditions for 'isBlockElement' prop application" mdxType="Note">
  The <Code mdxType="Code">isBlockElement</Code> prop only applies when the children of the{" "}
  <Code mdxType="Code">
    <Tooltip mdxType="Tooltip" />
  </Code>{" "}
  is either a string or multiple elements. They will be wrapped in a span or a
  div based on the prop value. If there is only one child element, the necessary
  props will be passed onto it.
    </Note>
    <h2>{`JS module reference`}</h2>
    <p>{`All elements with `}<inlineCode parentName="p">{`[data-tooltip]`}</inlineCode>{` attribute will be initialised automatically. Each tooltip requires a trigger element with `}<inlineCode parentName="p">{`[aria-describedby=tooltip-id]`}</inlineCode>{`.`}</p>
    <h3>{`Custom initialisation`}</h3>
    <CodeBlock language="jsx" mdxType="CodeBlock">
  {`
const myTooltip = new window.myApp.Tooltip(document.getElementById('my-tooltip'), config );
  `}
    </CodeBlock>
    <h4>{`Config`}</h4>
    <Table className="text-small" columns={[{
      key: "col1",
      label: "Name"
    }, {
      key: "col2",
      label: "Description."
    }]} data={[{
      col1: "rootContainerSelector - string",
      col2: "Selector for tooltip container. All tooltip dialogs are moved here on init."
    }, {
      col1: "popperOptions - object",
      col2: <React.Fragment>
          <p className="text-small">
            <a href="https://popper.js.org/popper-documentation.html#defaults">
              Popper.js
            </a>{" "}
            options object.
          </p>
          Default:
          <CodeBlock mdxType="CodeBlock">
            {`{
placement: 'top',
removeOnDestroy: true,
modifiers: {
  arrow: {
    element: '.tooltip__arrow',
  },
},
}`}
          </CodeBlock>
        </React.Fragment>
    }]} mdxType="Table" />
    <h3>{`Methods`}</h3>
    <Table className="text-small" columns={[{
      key: "col1",
      label: "Name"
    }, {
      key: "col2",
      label: "Description."
    }]} data={[{
      col1: "init",
      col2: "Creates a new instance"
    }, {
      col1: "destroy",
      col2: "Destroy the instance - removes all listeners, destroys Popper.js instance and removes the dialog element"
    }, {
      col1: "update",
      col2: "Updates Popper.js instance"
    }, {
      col1: "show",
      col2: "Shows the tooltip dialog"
    }, {
      col1: "hide",
      col2: "Hides the tooltip dialog"
    }, {
      col1: "static getInstance(el: element)",
      col2: "Get instance of `Tooltip` attached to specific DOM element (usually `[data-tooltip]`)."
    }]} mdxType="Table" />
    <h2>{`Accessibility`}</h2>
    <p>{`The tooltip component is implemented by the incomplete `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria-practices/#tooltip"
      }}>{`WAI-ARIA authoring practice`}</a>{` and `}<a parentName="p" {...{
        "href": "https://inclusive-components.design/tooltips-toggletips/"
      }}>{`Heydon Pickering`}</a>{`'s `}<a parentName="p" {...{
        "href": "https://inclusive-components.design/tooltips-toggletips/"
      }}>{`tooltip component`}</a>{`.`}</p>
    <p>{`Notes:`}</p>
    <ul>
      <li parentName="ul">{`The tooltip trigger element uses `}<inlineCode parentName="li">{`aria-owns`}</inlineCode>{` attribute to help NVDA speak the description text.`}</li>
      <li parentName="ul">{`In some situations, VoiceOver in Safari might not announce the tooltip tied to `}<inlineCode parentName="li">{`span`}</inlineCode>{`s without a specific role`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;